import { blankSpans, el, replaceChildrenWith, untimedSpans } from "./dom";
import { channelLabel, duration, languageName, outcomeLabel, positionLabel } from "./format";
import {
  NOT_RECORDED,
  decimalMinutes,
  sessionsForPerson,
  spokenSeconds,
  turnStamp
} from "./speakingTurns";
import type { SpeakingSession, TurnIndex } from "./speakingTurns";
import type { DataAvailability, SignUpRef, SpeakerRecord } from "./types";

/**
 * AB#9981. The speaker profile card.
 *
 * Anchored over the row that opened it rather than centred, so the reader keeps
 * their place in a long directory; a bottom sheet under 640px, where an anchored
 * card cannot fit. Focus is moved in, trapped, and RESTORED to the trigger, because
 * a keyboard user who closes this card and lands at the top of an 8,000px document
 * has effectively lost their work.
 */

export interface ProfileOptions {
  person: SpeakerRecord;
  availability: DataAvailability;
  /** Every speaking turn on the meeting, indexed once per render. The Speaking sessions
   *  table below is drawn from this person's slice of it. */
  turns: TurnIndex;
  trigger: HTMLElement;
  /** Where the card is appended. The report root, so page CSS applies. */
  host: HTMLElement;
}

type SpokeOnColumn = "item" | "outcome" | "position" | "length";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function openProfile(options: ProfileOptions): () => void {
  const { person, availability, turns, trigger, host } = options;

  const titleId = `spr-profile-title-${person.userId.replace(/[^A-Za-z0-9_-]/g, "")}`;
  const card = el("div", {
    class: "spr-profile",
    "data-testid": "speaker-report-profile",
    role: "dialog",
    "aria-modal": "true",
    "aria-labelledby": titleId,
    "data-user-id": person.userId
  });

  let sort: { column: SpokeOnColumn; direction: "ascending" | "descending" } = {
    column: "item",
    direction: "ascending"
  };

  const close = (): void => {
    document.removeEventListener("keydown", onKeyDown, true);
    document.removeEventListener("mousedown", onOutside, true);
    if (card.parentNode) card.parentNode.removeChild(card);
    // Restore, not merely blur. The trigger is where the reader was.
    trigger.focus();
  };

  function onKeyDown(event: KeyboardEvent): void {
    if (event.key === "Escape") {
      event.stopPropagation();
      close();
      return;
    }

    if (event.key !== "Tab") return;

    const focusable = Array.prototype.slice.call(
      card.querySelectorAll(FOCUSABLE)
    ) as HTMLElement[];
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function onOutside(event: MouseEvent): void {
    const target = event.target as Node | null;
    // Guard against a re-render having detached the original target: `contains` on a
    // node that is no longer in the document is false, which would close the card
    // every time a control inside it re-drew itself.
    if (!target || !target.isConnected) return;
    if (card.contains(target) || trigger.contains(target)) return;
    close();
  }

  const draw = (): void => {
    replaceChildrenWith(card, [
      header(person, titleId, close),
      stats(person),
      contact(person, availability),
      segments(person),
      speakingSessions(person, turns, availability),
      spokeOn(person, sort, (column) => {
        sort = {
          column,
          direction:
            sort.column === column && sort.direction === "ascending"
              ? "descending"
              : "ascending"
        };
        draw();

        // Sorting must not close the card. Re-focus the header that was activated,
        // which also keeps the keyboard user where they were.
        const header = card.querySelector<HTMLElement>(`[data-spokeon-sort="${column}"]`);
        header?.focus();
      })
    ]);
  };

  draw();
  host.appendChild(card);
  position(card, trigger);

  const firstFocusable = card.querySelector<HTMLElement>(FOCUSABLE);
  firstFocusable?.focus();

  document.addEventListener("keydown", onKeyDown, true);
  document.addEventListener("mousedown", onOutside, true);

  return close;
}

function header(person: SpeakerRecord, titleId: string, close: () => void): HTMLElement {
  const closeButton = el("button", {
    type: "button",
    class: "spr-profile-close",
    "data-testid": "speaker-report-profile-close",
    "aria-label": `Close the profile for ${person.name}`,
    text: "×"
  });
  closeButton.addEventListener("click", close);

  return el("div", { class: "spr-profile-head" }, [
    el("div", { class: "spr-profile-id" }, [
      el("h3", { id: titleId, class: "spr-profile-name", text: person.name }),
      person.organization
        ? el("p", { class: "spr-profile-org", text: person.organization })
        : null,
      el("p", { class: "spr-profile-channel", text: channelLabel(person.channel) })
    ]),
    closeButton
  ]);
}

function stats(person: SpeakerRecord): HTMLElement {
  const tiles = [
    stat("Sign-ups", String(person.signUpCount)),
    stat("Spoke on", String(person.spokeCount)),
    stat("Total time", duration(person.totalSpeakingSeconds))
  ];

  // AB#9981. There is deliberately NO registration instant here.
  //
  // Los Angeles County stated the constraint on the 2026-09-10 call: "one of the
  // requirements is we don't show an order of speakers that's driven by when they
  // registered. So their registration time stamps would not be in this report. In case
  // their order was different." Speakers are not necessarily called in the order they
  // signed up, and a per-person sign-up stamp on the record reads as evidence that they
  // were. Career counts below say "first-timer or regular" without dating anyone.
  //
  // Aggregate lead-time statistics -- how far ahead people register, with no per-person
  // instant and no order -- are a separate product question and are not what this removes.

  // Shown only when it says something. Every speaker has attended at least this meeting,
  // so a "1" here is not a finding -- it is the reader looking at the page they are on.
  if (person.careerMeetingCount > 1) {
    tiles.push(stat("Meetings attended", String(person.careerMeetingCount)));
    tiles.push(stat("Spoke, all meetings", String(person.careerSpokeCount)));
  }

  return el("div", { class: "spr-profile-stats" }, tiles);
}

function stat(label: string, value: string): HTMLElement {
  return el("div", { class: "spr-profile-stat" }, [
    el("span", { class: "spr-profile-stat-v", text: value }),
    el("span", { class: "spr-profile-stat-k", text: label })
  ]);
}

/**
 * Contact detail, shaped like the CRM profile: one LABEL per kind, with each value
 * on its own line beneath it. Two email addresses are two lines under one Email
 * heading, not two headings both reading Email.
 */
function contact(person: SpeakerRecord, availability: DataAvailability): HTMLElement {
  if (!availability.contactDetail) {
    return el("p", {
      class: "spr-profile-withheld",
      text: "Contact details are not shown for your account."
    });
  }

  const fields = el("div", { class: "spr-profile-fields" });
  let any = false;

  for (const [label, values] of [
    ["Email", person.emails],
    ["Phone", person.phones],
    ["Address", person.addresses]
  ] as Array<[string, string[]]>) {
    if (values.length === 0) continue;
    any = true;

    fields.appendChild(el("div", { class: "spr-field", "data-field": label.toLowerCase() }, [
      el("span", { class: "spr-field-k", text: label }),
      el("div", { class: "spr-field-vs" },
        values.map((value) =>
          el("span", { class: "spr-field-v", "data-field-value": "", text: value })
        ))
    ]));
  }

  if (!any) {
    return el("p", {
      class: "spr-profile-withheld",
      text: "This speaker gave no contact details when they registered."
    });
  }

  return fields;
}

function segments(person: SpeakerRecord): HTMLElement | null {
  if (person.segments.length === 0) return null;

  return el("div", { class: "spr-profile-segments" },
    person.segments.map((segment) =>
      el("span", {
        class: "spr-chip spr-chip-segment",
        "data-audience-id": segment.audienceId,
        text: segment.name
      })));
}

/**
 * AB#9981. Speaking sessions -- one row per TURN, with both endpoints and the real length.
 *
 * <b>The columns are the customer's, in the customer's order.</b> Los Angeles County sent
 * the layout they keep by hand today -- `# | Speaker Name | Start Time | End Time | Total
 * Time Spoken | Language | In Person/Virtual | Remarks/Agenda Item` -- and asked for "the
 * individual speaker's start and stop talk times and the exact amount of time spoken". A
 * table that carries the same facts in a different order is a table they have to re-arrange
 * before it can replace the sheet, so the order is copied rather than improved on.
 *
 * <b>Speaker Name repeats on every row.</b> Redundant inside a card that is already titled
 * with the name, and kept anyway: this is the block a clerk copies out into a minutes
 * document, and a pasted block with no name in it is a block that has to be re-labelled.
 *
 * <b>An open turn is stated, never estimated.</b> "Not recorded" in the end and length
 * cells, excluded from the total, and the total says how many it excluded. The alternative
 * -- measuring to now -- produces a number that grows on every render of a meeting that
 * ended months ago.
 *
 * <b>Language and channel come off the registration</b>, resolved in speakingTurns.ts, not
 * from a second heuristic here. There is one channel heuristic in this codebase.
 */
function speakingSessions(
  person: SpeakerRecord,
  turns: TurnIndex,
  availability: DataAvailability
): HTMLElement | null {
  // No timeline on this meeting means no turn was ever stamped, and a section headed
  // "Speaking sessions" that can only say "none recorded" for every speaker is noise. The
  // per-PERSON empty case below is different: there the meeting has turns and this person
  // has none, which is a fact about them worth stating.
  if (!availability.timeline || !turns.any) return null;

  const sessions = sessionsForPerson(turns, person.userId);

  const section = el("div", {
    class: "spr-profile-sessions",
    "data-testid": "speaker-report-speaking-sessions",
    "data-session-count": sessions.length
  });

  section.appendChild(el("h4", {
    class: "spr-profile-sessions-h",
    text: "Speaking sessions"
  }));

  // Which clock, said out loud, in the one place it can be said once for the whole table.
  section.appendChild(el("p", {
    class: "spr-profile-sessions-note",
    "data-clock-source": turns.clock.fromServer ? "meeting" : "browser",
    text: turns.clock.fromServer
      ? `Times are in ${turns.clock.timeZoneId || "the meeting's time zone"}.`
      : "The meeting's time zone could not be resolved, so times are shown in your"
        + " browser's time zone."
  }));

  if (sessions.length === 0) {
    section.appendChild(el("p", {
      class: "spr-empty",
      "data-testid": "speaker-report-speaking-sessions-empty",
      text: "No speaking turn recorded."
    }));
    return section;
  }

  const columns = [
    "#",
    "Speaker Name",
    "Start Time",
    "End Time",
    "Total Time Spoken",
    "Language",
    "In Person/Virtual",
    "Remarks/Agenda Item"
  ];

  // A real <table>. The rosters are grids because their headers are activatable buttons;
  // nothing here sorts -- these rows are a chronological record -- so the native element
  // is both simpler and better announced, and it gets a real <caption> and <th scope>.
  const table = el("table", { class: "spr-sessions-table" });
  table.appendChild(el("caption", {
    class: "spr-sr",
    text: `Speaking sessions for ${person.name}`
  }));

  const head = el("tr", {});
  for (const label of columns) {
    head.appendChild(el("th", { scope: "col", text: label }));
  }
  table.appendChild(el("thead", {}, [head]));

  const body = el("tbody", {});

  for (let index = 0; index < sessions.length; index += 1) {
    body.appendChild(sessionRow(sessions[index], index + 1, person, turns));
  }

  table.appendChild(body);
  table.appendChild(el("tfoot", {}, [totalRow(sessions, columns.length)]));

  section.appendChild(table);
  return section;
}

function sessionRow(
  session: SpeakingSession,
  ordinal: number,
  person: SpeakerRecord,
  turns: TurnIndex
): HTMLElement {
  const open = session.endMs === null;

  const cells: HTMLElement[] = [
    el("td", { class: "spr-num", text: String(ordinal) }),
    el("td", { text: person.name }),
    el("td", {
      class: "spr-num",
      "data-started-utc": new Date(session.startMs).toISOString(),
      text: turnStamp(session.startMs, turns.clock)
    }),
    el("td", {
      class: open ? "spr-untimed" : "spr-num",
      "data-ended-utc": open ? null : new Date(session.endMs as number).toISOString(),
      text: turnStamp(session.endMs, turns.clock)
    }),
    el("td", {
      class: open ? "spr-untimed" : "spr-num",
      "data-spoken-seconds": session.seconds === null ? null : session.seconds,
      text: decimalMinutes(session.seconds)
    }),
    // The registration's language, by name -- the same form the roster and directory use.
    // Blank is a real answer here (most speakers state none), so it gets the report's blank
    // treatment rather than an empty, broken-looking cell.
    languageName(session.languageCode)
      ? el("td", { "data-language-code": session.languageCode, text: languageName(session.languageCode) })
      : el("td", {}, blankSpans("None given")),
    el("td", { text: session.channel === "Phone" ? "Virtual" : "In person" }),
    el("td", {
      text: session.itemNumber || session.itemTitle || "—"
    })
  ];

  const row = el("tr", {
    "data-testid": "speaker-report-speaking-session-row",
    "data-agenda-item-id": session.agendaItemId,
    "data-open-turn": open ? "true" : "false"
  }, cells);

  return row;
}

function totalRow(sessions: SpeakingSession[], columnCount: number): HTMLElement {
  const { seconds, excluded } = spokenSeconds(sessions);

  const label = el("th", {
    scope: "row",
    // Everything left of Total Time Spoken: #, Speaker Name, Start Time, End Time.
    colspan: 4,
    text: "Total"
  });

  const value = el("td", {
    class: seconds === null ? "spr-untimed" : "spr-num spr-total",
    "data-testid": "speaker-report-speaking-sessions-total",
    "data-spoken-seconds": seconds === null ? null : seconds,
    text: decimalMinutes(seconds)
  });

  // The exclusion is stated ON the sum, not in a footnote somewhere else on the card. A
  // total that silently drops a turn is the defect; a total that says which turns it could
  // not add is a finding the clerk can act on.
  const note = el("td", {
    colspan: columnCount - 5,
    class: "spr-bar-note",
    text: excluded === 0
      ? ""
      : excluded === 1
        ? `1 turn is "${NOT_RECORDED}" and is not in this total.`
        : `${excluded} turns are "${NOT_RECORDED}" and are not in this total.`
  });

  return el("tr", { class: "spr-sessions-total" }, [label, value, note]);
}

function spokeOn(
  person: SpeakerRecord,
  sort: { column: SpokeOnColumn; direction: "ascending" | "descending" },
  onSort: (column: SpokeOnColumn) => void
): HTMLElement {
  const columns: Array<{ key: SpokeOnColumn; label: string }> = [
    { key: "item", label: "Agenda item" },
    { key: "position", label: "Position" },
    { key: "outcome", label: "Outcome" },
    { key: "length", label: "Length" }
  ];

  const head = el("div", { class: "spr-row spr-row-head", role: "row" },
    columns.map((column) => {
      const active = sort.column === column.key;
      const direction = active ? sort.direction : "ascending";

      const button = el("button", {
        type: "button",
        class: "spr-sort" + (active ? " is-active" : ""),
        "data-spokeon-sort": column.key,
        "data-sort-direction": direction,
        "aria-label": active
          ? `${column.label}, sorted ${direction}.`
          : `${column.label}, not sorted. Activate to sort ascending.`,
        text: column.label
      });

      button.addEventListener("click", () => onSort(column.key));
      return el("div", { class: "spr-cell spr-cell-head", role: "columnheader" }, [button]);
    }));

  const rows = sortSpokeOn(person.spokeOn, sort).map((reference) =>
    el("div", {
      class: "spr-row",
      role: "row",
      "data-spokeon-row": reference.signUpId
    }, [
      el("div", { class: "spr-cell", role: "gridcell" }, [
        el("span", { class: "spr-item-no", text: reference.itemNumber }),
        el("span", { class: "spr-item-title", text: reference.itemTitle })
      ]),
      el("div", { class: "spr-cell", role: "gridcell", text: positionLabel(reference.position) }),
      el("div", { class: "spr-cell", role: "gridcell", text: outcomeLabel(reference.outcome) }),
      el("div", { class: "spr-cell", role: "gridcell" },
        reference.speakingSeconds === null || reference.speakingSeconds === undefined
          ? untimedSpans()
          : [el("span", { class: "spr-len", text: duration(reference.speakingSeconds) })])
    ]));

  return el("div", { class: "spr-profile-spokeon", role: "grid" }, [head, ...rows]);
}

function sortSpokeOn(
  rows: SignUpRef[],
  sort: { column: SpokeOnColumn; direction: "ascending" | "descending" }
): SignUpRef[] {
  const flip = sort.direction === "ascending" ? 1 : -1;

  const key = (reference: SignUpRef): string | number | null => {
    switch (sort.column) {
      case "item": return `${reference.itemNumber}|${reference.itemTitle}`.toLowerCase();
      case "position": return positionLabel(reference.position).toLowerCase();
      case "outcome": return outcomeLabel(reference.outcome).toLowerCase();
      case "length": return reference.speakingSeconds;
      default: return null;
    }
  };

  return rows.slice().sort((a, b) => {
    const left = key(a);
    const right = key(b);

    // Nulls last in both directions: an untimed turn is an absence, not a short one.
    if (left === null && right === null) return a.signUpId.localeCompare(b.signUpId) * flip;
    if (left === null) return 1;
    if (right === null) return -1;
    if (left < right) return -1 * flip;
    if (left > right) return 1 * flip;
    return a.signUpId.localeCompare(b.signUpId) * flip;
  });
}

/**
 * Anchors the card above its trigger, shrinking it to fit rather than pushing it off
 * screen, and WITHOUT scrolling the page: the reader asked to look at one row, not
 * to be moved somewhere else in the document.
 */
function position(card: HTMLElement, trigger: HTMLElement): void {
  // The presentation is stated as data, not left to be inferred from a class name. Which
  // of the two shapes the profile took is a fact about the component's behaviour, and a
  // reader of the DOM -- or a test asserting AC-39 -- should not have to know that
  // "is-sheet" is the styling hook that happens to mean "bottom sheet".
  if (window.matchMedia("(max-width: 639px)").matches) {
    card.classList.add("is-sheet");
    card.setAttribute("data-presentation", "bottom-sheet");
    return;
  }

  card.setAttribute("data-presentation", "anchored");

  const anchor = trigger.getBoundingClientRect();
  const gap = 12;
  const width = Math.min(620, window.innerWidth - 32);

  card.style.width = `${width}px`;

  // Measure after the width is applied, so height reflects the real wrap.
  const height = card.offsetHeight;
  const above = anchor.top - gap;

  if (height > above) {
    card.style.maxHeight = `${Math.max(180, above)}px`;
    card.style.overflowY = "auto";
  }

  const top = Math.max(8, anchor.top - gap - Math.min(height, above));
  const left = Math.min(
    Math.max(8, anchor.left),
    Math.max(8, window.innerWidth - width - 8)
  );

  card.style.position = "fixed";
  card.style.top = `${top}px`;
  card.style.left = `${left}px`;
}
